<script setup lang="ts">
import type { ModelTypes } from '~/services-v2/financial-bff/__generated__/zeus'
import type { FileStatusType, StatusInstallation } from '~/utils/installation/Installation'
import { useToast } from '@solfacil/girassol'
import { toTypedSchema } from '@vee-validate/zod'
import JSZip from 'jszip'
import { useField, useForm } from 'vee-validate'
import * as zod from 'zod'
import ArrowRightIcon from '~icons/material-symbols/arrow-right-alt'
import DownloadIcon from '~icons/material-symbols/download-rounded'
import InfoIcon from '~icons/material-symbols/info-outline-rounded'
import PendingUpdateEquipmentAlert from '~/components/customer/alerts/PendingUpdateEquipmentAlert.vue'
import Tooltip from '~/components/tooltip/index.vue'
import { usePendingUpdateEquipment } from '~/composables/equipment/usePendingUpdateEquipment'
import useFinancing from '~/store-v2/financings/useFinancing'
import useInstallationDocument from '~/store-v2/installation/useInstallation'
import useInstallationHardwareUploadFile from '~/store-v2/installation/useInstallationHardwareUploadFile'

// initial hooks
const { t } = useI18n()
const router = useRouter()
const route = useRoute<any>()
const { createErrorToast } = useToast()

// constants
const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB

const initialValues = {
  uploadInverters: null,
  uploadInstallation: null,
  uploadAmpera: null,
  uploadInverterLabels: null,
  uploadBatteries: null,
  additionalComments: '',
}

const sectionLabels = {
  installation: t('installationPage.form.installationField.label'),
  inverters: t('installationPage.form.invertersField.label'),
  ampera: t('installationPage.form.installedField.label'),
  inverter_label: t('installationPage.form.inverterLabels.label'),
  datalogger_label: t('installationPage.form.dataloggerField.label'),
}

// initial variables and computeds
const id = computed(() => route.params.id as string)

// states and refs
const installationData = ref({} as ModelTypes['Installation'])
const statusInstallation = ref<StatusInstallation>({})
const showEmptyState = ref(false)
const loadingSendAnalysis = ref(false)
const openForm = ref(true)

// domain hooks
const {
  data: financing,
  isLoading,
} = useFinancing(id)

const {
  isLoading: isLoadingInstallation,
  refetch: refetchInstallation,
  data: installationDocument,
} = useInstallationDocument(id)

const { mutateAsync: uploadInstallationHardware } = useInstallationHardwareUploadFile()

const { hasPendingTAF } = usePendingUpdateEquipment(id)

onUpdated(() => {
  setErrorForm()
})

// computeds & derived states

const isStatusWaiting = computed(() => installationData?.value?.status?.slug === 'waiting')
const isStatusSuccess = computed(() => installationData?.value?.status?.slug === 'success')
const isStatusError = computed(() => installationData?.value?.status?.slug === 'error')
const isStatusProcessing = computed(() => installationData?.value?.status?.slug === 'processing')

const hasBattery = computed(() => Boolean(installationData.value?.sections?.find(item => item.type === 'battery_label')))
const hasInverterLabel = computed(() => Boolean(installationData.value?.sections?.find(item => item.type === 'inverter_label')))
const hasDataloggerLabel = computed(() => Boolean(installationData.value?.sections?.find(item => item.type === 'datalogger_label')))

const installationReproved = computed(() => Boolean(installationData?.value?.sections?.filter(section => (section.type === 'installation' && section?.status?.slug === 'reproved'))?.length))
const invertersReproved = computed(() => Boolean(installationData?.value?.sections?.filter(section => (section.type === 'inverters' && section?.status?.slug === 'reproved'))?.length))
const amperaReproved = computed(() => Boolean(installationData?.value?.sections?.filter(section => (section.type === 'ampera' && section?.status?.slug === 'reproved'))?.length))
const batteryReproved = computed(() => Boolean(installationData?.value?.sections?.filter(section => (section.type === 'battery_label' && section?.status?.slug === 'reproved'))?.length))
const loadings = computed(() => isLoadingInstallation.value || isLoading.value)
const hasManual = computed(() => installationData.value.has_ampera && installationData.value.has_minimal_projects && !isStatusSuccess.value)
const inverterLabelsReproved = computed(() => Boolean(installationData?.value?.sections?.filter(section => (section.type === 'inverter_label' && section?.status?.slug === 'reproved'))?.length))
const dataloggerLabelsReproved = computed(() => Boolean(installationData?.value?.sections?.filter(section => (section.type === 'datalogger_label' && section?.status?.slug === 'reproved'))?.length))

const installationStepData = computed(() => {
  if (!isLoadingInstallation.value) {
    if (!isStatusWaiting.value && openForm.value) {
      return {
        statusInstallation: statusInstallation.value,
        highlight: isStatusSuccess.value,
      }
    }

    if (isStatusWaiting.value && (!openForm.value || (openForm.value && installationData.value))) {
      return {
        statusInstallation: {
          pageTitle: t('installationPage.pageTitle'),
          title: t('installationPage.titleAddFiles'),
          subtitle: t('installationPage.descriptionAddFiles'),
          img: {
            name: 'installation-component-header.svg',
            alt: 'Logo de validação de arquivos',
            title: 'Logo de validação de arquivos',
          },
        },
        highlight: false,
      }
    }
  }
  return null
})

const installationStatusToShow = computed(() => {
  if (openForm.value && !installationData.value.has_ampera && !installationData.value.has_minimal_projects) {
    return {
      statusInstallation: statusInstallation.value,
      highlight: false,
    }
  }

  if (installationStepData.value) {
    return installationStepData.value
  }

  return {
    statusInstallation: {
      pageTitle: '',
      title: '',
      subtitle: '',
      img: {
        name: '',
        alt: '',
        title: '',
      },
    },
    highlight: false,
  }
})

const validationSchema = computed(() => {
  let schema = zod.object({
    uploadInstallation: zod
      .any()
      .refine(uploadInstallation => validateInputFile(uploadInstallation, 'installation'), 'É necessário anexar um arquivo.')
      .refine(uploadInstallation => validateSize(uploadInstallation, 'installation'), 'Todos os arquivos devem ter tamanho entre 1B e 20MB.'),
    uploadInverters: zod
      .any()
      .refine(uploadInverters => validateInputFile(uploadInverters, 'inverters'), 'É necessário anexar um arquivo.')
      .refine(uploadInverters => validateSize(uploadInverters, 'inverters'), 'Todos os arquivos devem ter tamanho entre 1B e 20MB.'),
    additionalComments: zod.string().optional(),
  })

  if (installationData?.value?.has_ampera) {
    schema = schema.extend({
      uploadAmpera: zod
        .any()
        .refine(uploadAmpera => validateInputFile(uploadAmpera, 'ampera'), 'É necessário anexar um arquivo.')
        .refine(uploadAmpera => validateSize(uploadAmpera, 'ampera'), 'Todos os arquivos devem ter tamanho entre 1B e 20MB.'),
    })
  }

  if (hasBattery.value) {
    schema = schema.extend({
      uploadBatteries: zod
        .any()
        .refine(uploadBattery => validateInputFile(uploadBattery, 'battery_label'), 'É necessário anexar um arquivo.')
        .refine(uploadBattery => validateSize(uploadBattery, 'battery_label'), 'Todos os arquivos devem ter tamanho entre 1B e 20MB.'),
    })
  }

  if (hasInverterLabel.value) {
    schema = schema.extend({
      uploadInverterLabels: zod
        .any()
        .refine(uploadInverterLabels => validateInputFile(uploadInverterLabels, 'inverter_label'), 'É necessário anexar um arquivo.')
        .refine(uploadInverterLabels => validateSize(uploadInverterLabels, 'inverter_label'), 'Todos os arquivos devem ter tamanho entre 1B e 20MB.'),
    })
  }

  if (hasDataloggerLabel.value) {
    schema = schema.extend({
      uploadDataloggerLabels: zod
        .any()
        .refine(uploadDataloggerLabels => validateInputFile(uploadDataloggerLabels, 'datalogger_label'), 'É necessário anexar um arquivo.')
        .refine(uploadDataloggerLabels => validateSize(uploadDataloggerLabels, 'datalogger_label'), 'Todos os arquivos devem ter tamanho entre 1B e 20MB.'),
    })
  }

  return toTypedSchema(schema)
})

const errorMessages = computed(() => {
  if (!installationData.value?.sections)
    return []

  return installationData.value.sections
    .filter(section => section.status.slug === 'reproved' && section.status.description)
    .map(section => ({ type: sectionLabels[section.type], description: section.status.description }))
})

// forms
const { handleSubmit, validate, setFieldError, setFieldValue } = useForm({
  validationSchema,
  initialValues,
})

// functions

function validateInputFile(field: any, type: string) {
  const filteredSections = installationData.value?.sections?.filter(section => (section.type === type && section?.status?.slug !== 'reproved'))

  if (!isStatusWaiting.value)
    return !!filteredSections?.length || (field !== null && field?.length > 0)
  else
    return (field !== null && field?.length > 0)
}

function validateSize(field: any, type: string) {
  const filteredSections = installationData.value?.sections?.filter(section => (section.type === type && section?.status?.slug !== 'reproved'))
  const sizeIsValid = (field !== null && field?.every((file: { size: number }) => file?.size > 0 && file?.size <= MAX_FILE_SIZE))

  if (!isStatusWaiting.value)
    return !!filteredSections?.length || sizeIsValid
  else
    return sizeIsValid
}

function setErrorForm() {
  if (installationData?.value?.sections?.length) {
    for (let section = 0; section < installationData.value.sections.length; section++) {
      if (installationData.value?.sections[section]?.status?.slug === 'reproved') {
        switch (installationData.value.sections[section].type) {
          case 'installation':
            setFieldError('uploadInstallation', installationData.value.sections[section].status.description)
            break
          case 'inverters':
            setFieldError('uploadInverters', installationData.value.sections[section].status.description)
            break
          case 'ampera':
            setFieldError('uploadAmpera' as any, installationData.value.sections[section].status.description)
            break
        }
      }
    }
  }
}

function setFieldAdditionalComments() {
  setFieldValue('additionalComments', installationData.value.comment)
}

function formattingInstallationInformations(status: FileStatusType, date: string, aboutInstallation: { has_ampera: boolean, has_ampera_activated: boolean, has_minimal_projects: boolean }): StatusInstallation {
  let nameStatus = ''

  // eslint-disable-next-line style/max-statements-per-line
  if (status !== 'waiting') { nameStatus = status }
  else {
    if (aboutInstallation.has_ampera && !aboutInstallation.has_minimal_projects && !aboutInstallation.has_ampera_activated)
      nameStatus = 'waitingMultiples'
    else if (aboutInstallation.has_ampera && aboutInstallation.has_minimal_projects && !aboutInstallation.has_ampera_activated)
      nameStatus = 'waiting'
    else if (!aboutInstallation.has_ampera && !aboutInstallation.has_minimal_projects && !aboutInstallation.has_ampera_activated)
      nameStatus = 'waitingWihtoutAmperaMultiples'
    else if (!aboutInstallation.has_ampera && aboutInstallation.has_minimal_projects && !aboutInstallation.has_ampera_activated)
      nameStatus = 'waitingWihtoutAmpera'
    else if (aboutInstallation.has_ampera_activated)
      nameStatus = 'waitingAmperaActivated'
  }

  const pageTitleKey = `installationPage.${nameStatus}.pageTitle`
  const titleKey = `installationPage.${nameStatus}.title`
  const subtitleKey = `installationPage.${nameStatus}.subtitle`
  const imgNameKey = `installationPage.${nameStatus}.img.name`
  const imgAltKey = `installationPage.${nameStatus}.img.alt`
  const imgTitleKey = `installationPage.${nameStatus}.img.title`

  const pageTitle = t(pageTitleKey)
  const title = t(titleKey)
  const subtitle = t(subtitleKey)

  const imgName = t(imgNameKey)
  const imgAlt = t(imgAltKey)
  const imgTitle = t(imgTitleKey)

  return {
    img: {
      name: imgName !== imgNameKey ? imgName : '',
      alt: imgAlt !== imgAltKey ? imgAlt : '',
      title: imgTitle !== imgTitleKey ? imgTitle : '',
    },
    pageTitle: pageTitle !== pageTitleKey ? pageTitle : '',
    title: title !== titleKey ? title : '',
    subtitle: nameStatus !== 'success' && subtitle !== subtitleKey ? subtitle : formatSubtitle(nameStatus as FileStatusType, date) ?? '',
  }
}

function getInstallationCompletionDate(date: string) {
  const dateObject = new Date(date)

  const day = dateObject.getDate().toString().padStart(2, '0')
  const month = (dateObject.getMonth() + 1).toString().padStart(2, '0')
  const year = dateObject.getFullYear()

  const formattedDate = `${day}/${month}/${year}`

  return formattedDate
}

function getInstallationCompletionHours(date: string) {
  const dateObject = new Date(date)

  const hours = dateObject.getHours().toString().padStart(2, '0')
  const minutes = dateObject.getMinutes().toString().padStart(2, '0')

  const formattedTime = `${hours}h${minutes}`

  return formattedTime
}

function formatSubtitle(status: FileStatusType, date: string) {
  if (!date)
    return

  const getDate = getInstallationCompletionDate(date)
  const getHour = getInstallationCompletionHours(date)

  return t(`installationPage.${status}.subtitle`, { date: getDate, hour: getHour })
}

function openHelp() {
  window.open('https://www.youtube.com/watch?v=sw3iOeBRbcs', '_blank')
}

function followManuallyOpenForm() {
  openForm.value = true
}

const sendAnalysis = handleSubmit(async (event: any) => {
  loadingSendAnalysis.value = true

  const formData = new FormData()

  event.uploadAmpera?.forEach((file: File) => {
    formData.append('files_ampera', file, file.name)
  })

  event.uploadInstallation?.forEach((file: File) => {
    formData.append('files_installation', file, file.name)
  })

  event.uploadInverters?.forEach((file: File) => {
    formData.append('files_inverters', file, file.name)
  })

  event.uploadInverterLabels?.forEach((file: File) => {
    formData.append('files_inverter_label', file, file.name)
  })

  event.uploadBatteries?.forEach((file: File) => {
    formData.append('files_battery_label', file, file.name)
  })

  event.uploadDataloggerLabels?.forEach((file: File) => {
    formData.append('files_datalogger_label', file, file.name)
  })

  const { valid } = await validate()

  if (valid) {
    try {
      const response = await uploadInstallationHardware({ installationId: installationData.value.id, files: formData, comment: event.additionalComments })
      if (response)
        await refetchInstallation()

      loadingSendAnalysis.value = false
    }
    catch {
      loadingSendAnalysis.value = false
      createErrorToast('Não foi possível concluir o processamento e envio dos arquivos. Por favor, tente novamente.')
    }
    finally {
      loadingSendAnalysis.value = false
    }
  }
})

function getFileTypeIcon(filetype: string) {
  return filetype.split('.')[1]
}

function getSectionData(section: ModelTypes['Section']) {
  const documents = section.documents
  return {
    'file-type-icon': documents?.map((doc) => {
      return getFileTypeIcon(doc?.filename || '')
    }),
    'file-name': documents?.map(doc => doc?.filename || ''),
    'status-type-document': section.status?.slug || '',
  }
}

function getSectionsByType(type: string) {
  return installationData?.value?.sections?.filter(section => section.type === type)
}

async function addFileToZip(zip, file) {
  try {
    const response = await fetch(file.url)
    if (!response.ok) {
      createErrorToast('Não foi possível baixar os arquivos. Por favor, tente novamente.')
      return
    }
    const blob = await response.blob()
    zip.file(file.filename, blob)
  }
  catch {
    createErrorToast('Não foi possível baixar os arquivos. Por favor, tente novamente.')
  }
}

async function downloadFiles() {
  const sections = installationData?.value?.sections
  if (!sections) {
    createErrorToast('Não foi possível baixar os arquivos. Por favor, tente novamente.')
    return
  }

  const files = sections.map(section => section.documents).flat()
  const zip = new JSZip()
  for (const file of files)
    await addFileToZip(zip, file)

  const content = await zip.generateAsync({ type: 'blob' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(content)
  link.download = 'instalacao.zip'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

function redirectFormalization() {
  if (financing.value?.person_type === 'PF')
    return router.push(`/customer/${id.value}`)

  return router.push(`/company/${id.value}`)
}

function monitorAmpera() {
  const url = `https://plataforma-ampera.solfacil.com.br/usinas/detalhes/${installationData.value.plant_id}`
  window.open(url, '_blank')
}

function openTrackAmperaLink() {
  const link = `
    https://solarinove.acompanharentrega.com.br/?tp=1&doc=${financing?.value?.document}`

  window.open(link, '_blank')
}

// watchers and observers
watch(isStatusWaiting, (value) => {
  if (value && openForm.value && installationData.value.has_ampera && installationData.value.has_minimal_projects) {
    openForm.value = false
  }
})

watch(() => installationDocument.value, async () => {
  if (installationDocument.value) {
    installationData.value = installationDocument.value as ModelTypes['Installation']
    showEmptyState.value = false

    const aboutInstallation = {
      has_ampera: installationData.value.has_ampera,
      has_ampera_activated: installationData.value.has_ampera_activated,
      has_minimal_projects: installationData.value.has_minimal_projects,
    }
    statusInstallation.value = formattingInstallationInformations(
      installationData?.value?.status?.slug,
      installationData.value?.concluded_at ?? '',
      aboutInstallation,
    )

    if (installationData.value.comment && installationData.value.comment !== 'undefined')
      setFieldAdditionalComments()
  }
}, { immediate: true })
</script>

<template>
  <div v-if="loadings" class="container">
    <div class="my-4 card-container flex flex-col justify-between w-full">
      <span class="loader-project" />
    </div>
  </div>
  <main v-else>
    <div class="installation-page">
      <NavigationHeaderBar
        v-if="!isLoadingInstallation"
        stage="installation"
        :title="installationData.project_name"
        :status="installationData?.status?.slug"
        :financing="financing"
      />
      <div v-if="!showEmptyState" class="container">
        <div v-if="isLoadingInstallation">
          <div class="loader-container">
            <span class="loader-project" />
          </div>
        </div>

        <div v-if="isStatusSuccess" class="w-full">
          <SharedBannerSolfacilPlus />
        </div>

        <InstallationValidationCard
          v-if="!isLoadingInstallation && !openForm"
          :status-installation="statusInstallation"
          :installation-data="installationData"
          :has-manual="hasManual"
          :is-status-waiting="isStatusWaiting"
          :open-form="openForm"
          @follow-manually-open-form="followManuallyOpenForm"
          @open-help="openHelp"
          @open-track-ampera-link="openTrackAmperaLink"
        />

        <div
          v-if="(!((isStatusWaiting && installationData.has_ampera && installationData.has_minimal_projects)) || (openForm))"
          class="w-full"
        >
          <div v-if="!isLoadingInstallation">
            <InstallationStepMessage
              v-if="installationStatusToShow"
              :status-installation="installationStatusToShow.statusInstallation"
              :highlight="installationStatusToShow.highlight"
              class="mt-6"
            />

            <PendingUpdateEquipmentAlert
              :title="t('customer.equipment_update.alert.pending_title')"
              :description="t('customer.equipment_update.alert.pending_description')"
              class="mt-6"
            />

            <SolAlert
              v-if="isStatusError"
              id="error-feedback"
              :title="errorMessages.length > 0 ? 'Um ou mais arquivos foram reprovadas pelos motivos abaixo' : 'A instalação foi reprovada'"
              feedback="error"
            >
              <div v-if="errorMessages.length > 0">
                <ul>
                  <li v-for="(error, index) in errorMessages" :key="index">
                    <span>{{ error.type }}: </span>
                    <span>{{ error.description }}</span>
                  </li>
                </ul>
              </div>
              <span v-else>{{ installationData.status.description }}</span>
            </SolAlert>
            <form :class="`${loadingSendAnalysis || (isStatusError && errorMessages.length === 0) ? 'pointer-events-none' : ''}`">
              <div class="grid grid-cols-1 md:system:grid-cols-2 gap-6 mt-6">
                <div>
                  <strong class="block text-3xs fonts-body-medium-bold">{{ t('installationPage.form.installationField.label') }}</strong>
                  <SolFileUpload
                    v-if="installationReproved || isStatusWaiting"
                    id="uploadInstallation"
                    name="uploadInstallation"
                    :use-field="useField"
                    :download-menu="['preview', 'download', 'delete']"
                    accept=".jpeg, .jpg, .png, .webp, .pdf, mp4"
                    :multiple="true"
                    :placeholder="t('installationPage.form.placeholderUpload')"
                    label=""
                  />
                  <template v-else>
                    <SharedMessageStatus
                      v-for="(section, index) in getSectionsByType('installation')"
                      :key="index"
                      v-bind="getSectionData(section)"
                    />
                  </template>
                </div>

                <div v-if="installationData.has_ampera">
                  <strong class="block text-3xs fonts-body-medium-bold">{{ t('installationPage.form.installedField.label') }}</strong>
                  <SolFileUpload
                    v-if="amperaReproved || isStatusWaiting"
                    id="uploadAmpera"
                    name="uploadAmpera"
                    :use-field="useField"
                    :download-menu="['preview', 'download', 'delete']"
                    accept=".jpeg, .jpg, .png, .webp, .pdf, mp4"
                    :multiple="true"
                    :placeholder="t('installationPage.form.placeholderUpload')"
                    label=""
                  />

                  <template v-else>
                    <SharedMessageStatus
                      v-for="(section, index) in getSectionsByType('ampera')"
                      :key="index"
                      v-bind="getSectionData(section)"
                    />
                  </template>
                </div>

                <div>
                  <strong class="block text-3xs fonts-body-medium-bold">{{ t('installationPage.form.invertersField.label') }}</strong>
                  <SolFileUpload
                    v-if="invertersReproved || isStatusWaiting"
                    id="uploadInverters"
                    name="uploadInverters"
                    :use-field="useField"
                    :download-menu="['preview', 'download', 'delete']"
                    accept=".jpeg, .jpg, .png, .webp, .pdf, mp4"
                    :multiple="true"
                    :placeholder="t('installationPage.form.placeholderUpload')"
                    label=""
                  />
                  <template v-else>
                    <SharedMessageStatus
                      v-for="(section, index) in getSectionsByType('inverters')"
                      :key="index"
                      v-bind="getSectionData(section)"
                    />
                  </template>
                </div>

                <div v-if="hasInverterLabel">
                  <strong class="flex flex-row items-center gap-1 text-3xs fonts-body-medium-bold">
                    {{ t('installationPage.form.inverterLabels.label') }}

                    <Tooltip position="top" class="labels-tooltip">
                      <template #text>
                        O <b>número de série (SN)</b> deve estar legível,
                        caso contrário a instalação não será validada
                      </template>

                      <InfoIcon class="icon" />
                    </Tooltip>
                  </strong>
                  <SolFileUpload
                    v-if="isStatusWaiting || inverterLabelsReproved"
                    id="uploadInverterLabels"
                    name="uploadInverterLabels"
                    :use-field="useField"
                    :download-menu="['preview', 'download', 'delete']"
                    accept=".jpeg, .jpg, .png, .webp, .pdf, mp4"
                    :multiple="true"
                    :placeholder="t('installationPage.form.placeholderUpload')"
                    label=""
                    :helper-text="t('installationPage.form.inverterLabels.helper')"
                  />
                  <template v-else>
                    <SharedMessageStatus
                      v-for="(section, index) in getSectionsByType('inverter_label')"
                      :key="index"
                      v-bind="getSectionData(section)"
                    />
                  </template>
                </div>

                <div v-if="hasDataloggerLabel">
                  <strong class="flex flex-row items-center gap-1">
                    {{ t('installationPage.form.dataloggerField.label') }}
                  </strong>
                  <SolFileUpload
                    v-if="isStatusWaiting || dataloggerLabelsReproved"
                    id="uploadDataloggerLabels"
                    name="uploadDataloggerLabels"
                    :use-field="useField"
                    :download-menu="['preview', 'download', 'delete']"
                    accept=".jpeg, .jpg, .png, .webp, .pdf, mp4"
                    :multiple="true"
                    :placeholder="t('installationPage.form.placeholderUpload')"
                    label=""
                  />
                  <template v-else>
                    <SharedMessageStatus
                      v-for="(section, index) in getSectionsByType('datalogger_label')"
                      :key="index"
                      v-bind="getSectionData(section)"
                    />
                  </template>
                </div>

                <div v-if="hasBattery">
                  <strong class="block text-3xs fonts-body-medium-bold">{{ t('installationPage.form.batteryInstalled') }}</strong>
                  <SolFileUpload
                    v-if="isStatusWaiting || batteryReproved"
                    id="uploadBatteries"
                    name="uploadBatteries"
                    :use-field="useField"
                    :download-menu="['preview', 'download', 'delete']"
                    accept=".jpeg, .jpg, .png, .webp, .pdf, mp4"
                    :multiple="true"
                    :placeholder="t('installationPage.form.placeholderUpload')"
                    label=""
                  />

                  <template v-else>
                    <SharedMessageStatus
                      v-for="(section, index) in getSectionsByType('battery_label')"
                      :key="index"
                      v-bind="getSectionData(section)"
                    />
                  </template>
                </div>
              </div>

              <div class="flex flex-row gap-1 mt-6 text-neutral-low-light leading-default">
                <ArrowRightIcon class="w-[14px] h-[14px]" />

                <p class="text-2xs">
                  <strong>{{ t('installationPage.descriptionAvoidFailures.part1') }}</strong>
                  {{ t('installationPage.descriptionAvoidFailures.part2') }}
                  <strong>{{ t('installationPage.descriptionAvoidFailures.part3') }}</strong>
                </p>
              </div>

              <div class="grid grid-cols-1  gap-6 mt-6">
                <div>
                  <strong class="block mb-quark text-3xs fonts-body-medium-bold">{{ t('installationPage.form.additionalComments') }}</strong>
                  <SolInputTextarea
                    id="additionalComments"
                    name="additionalComments"
                    :use-field="useField"
                    :disabled="isStatusProcessing || isStatusSuccess"
                    placeholder="Sinalize aqui qualquer alteração de marca, modelos de equipamentos, entre outros."
                  />
                </div>
              </div>
            </form>

            <SolDivider thickness="x-small" class="mt-10 mb-6" />

            <div class="flex justify-between">
              <SolButton
                id="downloadButton"
                variant="secondary"
                size="large"
                :disabled="isStatusWaiting"
                @click="downloadFiles()"
              >
                {{ t('installationPage.form.downloadFiles') }}
                <template #icon:left>
                  <DownloadIcon class="icon" />
                </template>
              </SolButton>
              <Tooltip
                v-if="hasPendingTAF"
                :text="t('inversorInstallation.form.taf_pending_tooltip')"
                position="top"
                width="400px"
              >
                <SolButton
                  id="sendButton"
                  :disabled="
                    hasPendingTAF
                      || (!installationReproved && !invertersReproved && !amperaReproved && !batteryReproved && !isStatusWaiting && !inverterLabelsReproved && !dataloggerLabelsReproved)
                      || Boolean(isStatusError && errorMessages.length === 0)"
                  variant="primary"
                  size="large"
                  :loading="loadingSendAnalysis"
                  @click="sendAnalysis()"
                >
                  {{ t('installationPage.form.sendButton') }}
                </SolButton>
              </Tooltip>
              <SolButton
                v-else
                id="sendButton"
                :disabled="
                  hasPendingTAF
                    || (!installationReproved && !invertersReproved && !amperaReproved && !batteryReproved && !isStatusWaiting && !inverterLabelsReproved && !dataloggerLabelsReproved)
                    || Boolean(isStatusError && errorMessages.length === 0)"
                variant="primary"
                size="large"
                :loading="loadingSendAnalysis"
                @click="sendAnalysis()"
              >
                {{ t('installationPage.form.sendButton') }}
              </SolButton>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="bg-neutral-high-pure w-full rounded-lg p-10  flex items-center justify-center">
        <SolEmptyState
          id="emptyStateInstallation"
          :title="$t('emptyStateInstallation.title')"
          :subtitle="$t('emptyStateInstallation.subtitle')"
          variant="no-content"
        />
      </div>
    </div>

    <div class="mx-4 md:system:mx-12">
      <SolDivider thickness="x-small" orientation="horizontal" class="my-5 mt-2 md:system:my-0 md:system:-mt-5" />

      <div class="flex flex-col md:system:flex-row flex-col-reverse justify-between gap-5 my-4 md:system:my-6">
        <SolButton
          id="installation-back-formalization"
          size="large"
          class="w-full md:system:w-auto"
          variant="secondary"
          @click="redirectFormalization"
        >
          {{ t('installationPage.footer.formalization') }}
        </SolButton>

        <SolButton
          v-if="installationData.has_ampera && installationData.has_minimal_projects && installationData.plant_id"
          id="installation-next-ampera"
          size="large"
          class="w-full md:system:w-auto"
          :disabled="!isStatusSuccess"
          @click="monitorAmpera"
        >
          {{ t('installationPage.footer.ampera') }}
        </SolButton>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.installation-page {
  @apply flex flex-col py-2xs px-4xs gap-4;

  @screen md:system {
    @apply px-2xs pb-sm pt-2xs;
  }

  .container {
    @apply bg-neutral-high-pure rounded-lg p-2xs;
  }

  .status {
    @apply flex items-center justify-center flex-col border p-6 rounded-lg border-neutral-high-dark;

    .info {
      @apply flex flex-col items-center justify-center lg:site:max-w-[1000px];
    }
  }

  .title-status {
    @apply text-sm mt-4 text-center leading-8 font-highlight;
  }

  .subtitle-status {
    @apply text-center text-2xs text-neutral-low-light lg:site:max-w-[800px] font-regular leading-xs;
  }

  .buttons {
    @apply flex pt-2xs gap-4 flex-col items-center md:site:flex-row-reverse;

    &.has-manual {
      @apply md:site:flex-col;
    }

    .icon {
      @apply h-6 w-6;
    }
  }

  .experience {
    @apply flex justify-end pt-6 mt-4 border-t border-neutral-high-dark;
  }

  .loader-container {
    @apply  my-4 flex flex-col justify-between w-full;
  }

  .loader-project {
    @apply h-96 rounded-lg;
    @apply top-0 right-0 bottom-0 left-0 z-50;
    background: rgba(#fff, 0.9)
      url(https://cdn.solfacil.com.br/assets/img/loading-for-legacy.gif) no-repeat
      center center;
  }

  .labels-tooltip {
    @apply my-auto;
    .icon {
      @apply w-4xs h-4xs;
    }

    .tooltip {
      margin-top: 20px !important;
      width: 330px !important;
    }
  }
}
</style>
